import { useState, useRef, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { useApiClient } from '../../api/ApiClientContext';
import { UserFlowStatusResponse } from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import { Fragment } from 'react/jsx-runtime';
import styles from './PlatformOnboarding.module.scss';

function isVideoAtOrPastEnd(video: HTMLVideoElement): boolean {
  if (video.ended) return true;
  const { duration, currentTime } = video;
  if (!Number.isFinite(duration) || duration <= 0) return false;
  return currentTime >= duration - 0.2;
}

const objectivesHero = '/assets/img/onboarding/objectives-hero.webp';
const objectivesTree1 = '/assets/img/onboarding/objectives-tree-1.webp';
const objectivesTree2 = '/assets/img/onboarding/objectives-tree-2.webp';
const initiativeSummary = '/assets/img/onboarding/initiative-summary-1eb12a.webp';

type IconName = 'KeepTrackIcon' | 'DevelopBehaviours' | 'BlueArrowRight' | 'BringCulture';

interface StepDef {
  id: string;
  component: React.ReactNode;
  hasBack: boolean;
  nextDisabled?: boolean;
}

interface PlatformOnboardingProps {
  userFlowStatus: UserFlowStatusResponse;
}

/* ── CEO Video Screen ── */
const CeoVideoStep = ({
  videoUrl,
  title,
  body,
  onReachedEnd,
  onVideoUnavailable,
}: {
  videoUrl: string;
  title: string;
  body?: string | null;
  onReachedEnd: () => void;
  onVideoUnavailable: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reportedRef = useRef(false);

  const reportReachedEnd = useCallback(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    onReachedEnd();
  }, [onReachedEnd]);

  const checkProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isVideoAtOrPastEnd(video)) {
      reportReachedEnd();
    }
  }, [reportReachedEnd]);

  return (
    <div className={styles.content}>
      <div className={styles.videoWrapper}>
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          onTimeUpdate={checkProgress}
          onSeeked={checkProgress}
          onEnded={reportReachedEnd}
          onError={onVideoUnavailable}
        />
      </div>
      <div className={styles.introTitle}>{title}</div>
      {body && (
        <div className={styles.introBody}>
          <ReactMarkdown>{body}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

/* ── Welcome Screen ── */
const welcomeIcons: { title: string; text: string; iconName: IconName }[] = [
  {
    title: 'Keep track of your progress',
    text: 'Commit to selected habits at least 30 times during 60 days',
    iconName: 'KeepTrackIcon',
  },
  {
    title: 'Develop behaviours',
    text: 'By committing to habits and tracking your progress',
    iconName: 'DevelopBehaviours',
  },
  {
    title: 'Bring culture change',
    text: 'With science and psychology behind team habits',
    iconName: 'BringCulture',
  },
];

const WelcomeStep = () => (
  <div>
    <div className={styles.welcomeGreet}>
      <h1 className={styles.welcomeTitle}>Welcome to HabiTrack</h1>
      <h3 className={styles.welcomeSubtitle}>
        Science and psychology of habit formation
      </h3>
      <p className={styles.welcomeDescription}>
        Habits support your personal development and learning journey. Keep
        track of your progress and endorse your teammates effort. You can also
        see what your team are working towards and their individual and
        collective progress.
      </p>
    </div>
    <div className={styles.welcomeIcons}>
      {welcomeIcons.map((item, idx) => (
        <Fragment key={item.title}>
          {idx > 0 && (
            <Icon iconName="BlueArrowRight" width="1.3rem" height="1.4rem" />
          )}
          <div className={styles.welcomeIconItem}>
            <Icon iconName={item.iconName} width="5.88rem" height="5.88rem" />
            <div className={styles.welcomeIconTitle}>{item.title}</div>
            <div className={styles.welcomeIconText}>{item.text}</div>
          </div>
        </Fragment>
      ))}
    </div>
  </div>
);

/* ── Objectives Framework (Leader) ── */
const ObjectivesFrameworkStep = () => (
  <div className={styles.content}>
    <div className={styles.roleSection}>
      <h2 className={styles.roleScreenTitle}>
        Organisational objectives framework
      </h2>
      <p className={styles.roleScreenDescription}>
        HabiTrack helps you turn strategic objectives into measurable progress
        by linking day-to-day behaviours to the outcomes your organisation cares
        about. The platform is built around a simple hierarchy that lets you
        move from high-level outcomes to the initiatives and behaviours
        influencing them.
      </p>
      <img
        src={objectivesHero}
        alt="Objectives overview"
        className={styles.roleScreenshot}
      />
    </div>

    <div className={styles.roleSection}>
      <h3 className={styles.roleSectionHeading}>How does it work?</h3>
      <p className={styles.roleScreenDescription}>
        At the top level, you will see your objectives. From this view, you can
        quickly see which objectives are on track, which are at risk, and where
        performance is improving or declining over time.
      </p>
      <div className={styles.screenshotPair}>
        <img
          src={objectivesTree1}
          alt="Objective tree view"
          className={styles.roleScreenshotHalf}
        />
        <img
          src={objectivesTree2}
          alt="Objective detail view"
          className={styles.roleScreenshotHalf}
        />
      </div>
      <p className={styles.roleScreenDescription}>
        Each objective is presented as a tree, showing the goals that sit
        beneath it, KPIs used to measure success, and the initiatives that are
        contributing to that goal. This allows you to understand not just
        whether a goal is performing, but which programmes or focus areas are
        influencing it.
      </p>
      <p className={styles.roleScreenDescription}>
        This tree structure allows you to start at the strategic level and
        progressively explore why performance looks the way it does&nbsp;&mdash;&nbsp;from
        objectives, to goals and KPIs, to initiatives and behaviours. This
        gives you confidence that improvement is being driven by real, sustained
        behaviour change, and enables informed decisions about where to
        reinforce, adjust, or stop activity altogether.
      </p>
    </div>
  </div>
);

/* ── Initiative Insights (Sponsor) ── */
const InitiativeInsightsStep = () => (
  <div className={styles.content}>
    <div className={styles.roleSection}>
      <h2 className={styles.roleScreenTitle}>
        How does your organisation adopt initiatives?
      </h2>
      <p className={styles.roleScreenDescription}>
        The initiatives create the structural conditions for change, while the
        behaviours form the human foundation that turns strategy into reality.
      </p>
      <p className={styles.roleScreenDescription}>
        In this way, behaviours reinforce initiatives; initiatives drive
        progress toward our organisational objectives; objectives align to our
        goals; and goals are measured through our KPIs. Together, this creates a
        coherent system that enables every employee to contribute meaningfully
        to the culture we are building.
      </p>
      <img
        src={initiativeSummary}
        alt="Initiative summary"
        className={styles.roleScreenshot}
      />
    </div>

    <p className={styles.roleScreenDescription}>
      Drilling down into an initiative shows the behaviours being promoted
      within that initiative and how consistently they are being adopted. These
      behaviours are supported by simple habit tracking by employees, creating
      behavioural data that shows engagement and cultural development progress.
      You can see which behaviours are gaining traction, where uptake is
      uneven, and how this differs across initiatives.
    </p>
  </div>
);

/* ── Main Component ── */
const PlatformOnboarding = ({ userFlowStatus }: PlatformOnboardingProps) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [stepIndex, setStepIndex] = useState(0);
  const [videoEnded, setVideoEnded] = useState(false);

  const { mutate: completePlatformOnboarding, isPending, isError, reset } = useMutation({
    mutationFn: () =>
      apiClient.api.completePlatformOnboardingApiV1AuthCompletePlatformOnboardingPost(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const markVideoComplete = useCallback(() => {
    setVideoEnded(true);
  }, []);

  const introTitle =
    userFlowStatus.platform_intro_title ?? 'Introduction from CEO';

  const ceoVideoUrl = userFlowStatus.ceo_video_url?.trim();

  const steps = useMemo(() => {
    const s: StepDef[] = [];

    if (ceoVideoUrl) {
      s.push({
        id: 'ceo_video',
        hasBack: false,
        nextDisabled: !videoEnded,
        component: (
          <CeoVideoStep
            videoUrl={ceoVideoUrl}
            title={introTitle}
            body={userFlowStatus.platform_intro_body}
            onReachedEnd={markVideoComplete}
            onVideoUnavailable={markVideoComplete}
          />
        ),
      });
    }

    s.push({
      id: 'welcome',
      hasBack: s.length > 0,
      component: <WelcomeStep />,
    });

    if (userFlowStatus.app_role?.is_leader) {
      s.push({
        id: 'objectives',
        hasBack: true,
        component: <ObjectivesFrameworkStep />,
      });
    }

    if (userFlowStatus.app_role?.is_sponsor) {
      s.push({
        id: 'initiative_insights',
        hasBack: true,
        component: <InitiativeInsightsStep />,
      });
    }

    return s;
  }, [
    ceoVideoUrl,
    userFlowStatus.platform_intro_body,
    userFlowStatus.app_role?.is_leader,
    userFlowStatus.app_role?.is_sponsor,
    introTitle,
    videoEnded,
    markVideoComplete,
  ]);

  const current = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      reset();
      completePlatformOnboarding();
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  return (
    <div className={styles.container}>
      <div className={styles.body}>{current?.component}</div>
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {current?.hasBack ? (
            <button type="button" className={styles.backBtn} onClick={handleBack}>
              Back
            </button>
          ) : null}
          {isError ? (
            <span className={styles.footerError} role="alert">
              Something went wrong. Please try again.
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.nextBtn}
          onClick={handleNext}
          disabled={current?.nextDisabled || isPending}
        >
          {isPending ? 'Saving…' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default PlatformOnboarding;
